
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Feedback } from './useFeedbackData';
import { FeedbackHeader } from './FeedbackHeader';
import { FeedbackSection } from './FeedbackSection';
import { NavigateFunction } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Folder } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

type FeedbackContentProps = {
  feedback: Feedback;
  formatDate: (date: string) => string;
  navigate: NavigateFunction;
};

export const FeedbackContent = ({ feedback, formatDate, navigate }: FeedbackContentProps) => {
  // Determine if this is a candidate feedback to recruiter
  const isCandidateFeedback = feedback?.feedback_type === 'candidat';
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { toast } = useToast();
  const [projectInfo, setProjectInfo] = useState<{id: string, name: string} | null>(null);

  // Load project info when feedback loads
  useEffect(() => {
    if (!feedback?.id || !user) return;
    
    const loadProjectInfo = async () => {
      try {
        console.log("Fetching project info for feedback:", feedback.id);
        const { data, error } = await supabase
          .from('projects_feedbacks')
          .select(`
            project_id,
            projects (id, name)
          `)
          .eq('feedback_id', feedback.id)
          .eq('user_id', user.id)
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching project info:", error);
          throw error;
        }
        
        console.log("Project info response:", data);
        
        if (data && data.projects) {
          setProjectInfo({
            id: data.project_id,
            name: data.projects.name
          });
          console.log("Found project info:", {id: data.project_id, name: data.projects.name});
        } else {
          console.log("No project associated with this feedback");
          setProjectInfo(null);
        }
      } catch (error) {
        console.error('Error loading project info:', error);
        toast({
          title: "Erreur",
          description: "Impossible de charger les informations du projet",
          variant: "destructive"
        });
      }
    };
    
    loadProjectInfo();
  }, [feedback?.id, user, toast]);

  // Ensure feedback object exists before rendering
  if (!feedback) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Impossible de charger les données du feedback</p>
        <Button 
          onClick={() => navigate('/dashboard')}
          className="mt-4"
        >
          Retour au tableau de bord
        </Button>
      </div>
    );
  }

  console.log("Rendering feedback content:", feedback, "Project info:", projectInfo);

  return (
    <>
      <Card className="mb-4 md:mb-6 shadow-sm">
        <div className="p-4 md:p-6">
          <FeedbackHeader 
            feedback={feedback} 
            formatDate={formatDate} 
            onBack={() => navigate('/dashboard')}
          />
          
          {/* Show project badge if available */}
          {projectInfo && (
            <div className="mt-4 flex items-center">
              <span className="text-sm text-gray-500 mr-2">Projet:</span>
              <Badge 
                variant="outline" 
                className="bg-blue-50 cursor-pointer hover:bg-blue-100"
                onClick={() => navigate(`/projects/${projectInfo.id}`)}
              >
                <Folder className="h-3 w-3 mr-1" />
                {projectInfo.name}
              </Badge>
            </div>
          )}
        </div>
      </Card>

      <div className="space-y-6">
        <FeedbackSection 
          title="Points forts" 
          content={feedback.positives} 
          titleColor="text-green-600"
          bgColor="bg-green-50"
        />

        <FeedbackSection 
          title="Axes d'amélioration" 
          content={feedback.improvements} 
          titleColor="text-orange-600"
          bgColor="bg-orange-50"
        />

        <FeedbackSection 
          title="Commentaire général" 
          content={feedback.general_comment}
          bgColor="bg-blue-50" 
        />

        <FeedbackSection 
          title="Message final" 
          content={feedback.final_message} 
          titleColor="text-blue-600"
          bgColor="bg-blue-50"
        />
      </div>

      <div className="flex justify-center mt-8">
        <Button 
          onClick={() => navigate('/dashboard')}
          size={isMobile ? "sm" : "default"}
          className="px-5 py-2 shadow-sm"
        >
          Retour au tableau de bord
        </Button>
      </div>
    </>
  );
};
